import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChatBubble } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  styled,
} from "@mui/material";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";

import {
  UpdateSubscriberSchema,
  UpdateSubscriberSchemaType,
} from "@stw/yup-validators";

import {
  GetAudienceContactFieldsQuery,
  GetThreadsForSubscriberQuery,
  GetThreadsForSubscriberQueryVariables,
  OptOutDeleteSourceType,
  ResubscribeSubscriberMutation,
  ResubscribeSubscriberMutationVariables,
  SubscriberSource,
  UnsubscribeSubscriberMutation,
  UnsubscribeSubscriberMutationVariables,
  UpdateSubscriberMutation,
  UpdateSubscriberMutationVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table from "~frontend/components/Table";
import { RhfErrorBanner } from "~frontend/components/forms/react-hook-form-components";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import {
  GET_SUBSCRIBER_SUMMARY,
  GET_THREADS_FOR_SUBSCRIBER,
  RESUBSCRIBE_SUBSCRIBER,
  UNSUBSCRIBE_SUBSCRIBER,
  UPDATE_SUBSCRIBER,
} from "~frontend/graphql/shortcode";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import {
  Subscriber,
  Audience,
} from "~frontend/routes/subaccounts/audiences/Subscribers/types";
import { createUpdateSubscriberInitialValues } from "~frontend/routes/subaccounts/audiences/Subscribers/utils";
import theme, { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

import { ContactFieldsEditor } from "./ContactFieldsEditor";
import { SubscriberAudience } from "./types";

const SubscriberDialogActions = styled(DialogActions)({
  justifyContent: "space-between",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  marginBottom: theme.spacing(1),
});

function SubscriberDetails({
  subscriber,
  subscriberAudience,
  editing = false,
  control,
  fieldDefinitions,
  showSaveSuccessful,
}: {
  subscriber: Subscriber;
  subscriberAudience: SubscriberAudience;
  editing?: boolean;
  control: any;
  fieldDefinitions: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  showSaveSuccessful: boolean;
}) {
  const { subaccountLink } = useSubaccount()!;

  const getThreadsQuery = useQuery<
    GetThreadsForSubscriberQuery,
    GetThreadsForSubscriberQueryVariables
  >(GET_THREADS_FOR_SUBSCRIBER, {
    variables: {
      input: { subscriberId: subscriber.id, audienceId: subscriberAudience.id },
    },
  });

  const historyRows: { message: string; timestamp: number }[] = [];

  // add a row for initial subscription
  switch (subscriber.source) {
    case SubscriberSource.Api:
      historyRows.push({
        message: "Subscribed via API",
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.ShortCodeKeyword:
      historyRows.push({
        message: `Subscribed via keyword`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.BulkUpload:
      historyRows.push({
        message: `Subscribed via bulk upload ${subscriber.createdByUser ? `by ${subscriber.createdByUser?.fullName}` : ""}`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.Manual:
      historyRows.push({
        message: `Subscribed manually ${subscriber.createdByUser ? `by ${subscriber.createdByUser?.fullName}` : ""}`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.ActBlueDonation:
      historyRows.push({
        message: `Subscribed via Act Blue donation`,
        timestamp: subscriber.createdAt,
      });
      break;
    case SubscriberSource.ActionKitFormSubmission:
      historyRows.push({
        message: "Subscribed via ActionKit form",
        timestamp: subscriber.createdAt,
      });
      break;
    default:
      subscriber.source satisfies never;
  }

  // Add rows for opt-out and opt-ins
  subscriber.optOuts.forEach(optOut => {
    switch (optOut.source.__typename) {
      case "OptOutAdminListSource":
        historyRows.push({
          message: `Opted out via bulk upload`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutAdminManualSource":
        historyRows.push({
          message: `Opted out manually ${optOut.createdByUser ? `by ${optOut.createdByUser?.fullName}` : ""}`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutCampaignAutomaticSource":
        historyRows.push({
          message: `Opted out automatically via keyword`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutCampaignManualSource":
        historyRows.push({
          message: `Opted out manually ${optOut.createdByUser ? `by ${optOut.createdByUser?.fullName}` : ""}`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutShortCodeKeywordSource":
        historyRows.push({
          message: `Opted out via short code keyword`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutShortCodeAutomaticSource":
        historyRows.push({
          message: `Opted out via automatic opt out keyword`,
          timestamp: optOut.createdAt,
        });
        break;
      case "OptOutSpokeSource":
        historyRows.push({
          message: `Opted out via Spoke`,
          timestamp: optOut.createdAt,
        });
        break;
      case undefined:
        historyRows.push({
          message: `Opted out`,
          timestamp: optOut.createdAt,
        });
        break;
      default:
        optOut.source satisfies never;
    }

    const deleteType = optOut.deleteSourceType;
    if (deleteType) {
      switch (deleteType) {
        case OptOutDeleteSourceType.AdminList:
          historyRows.push({
            message: `Resubscribed via bulk upload`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.AdminManual:
          historyRows.push({
            message: `Opt out removed manually ${optOut.deletedByUser ? `by ${optOut.deletedByUser?.fullName}` : ""}`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.ApiResubscribe:
          historyRows.push({
            message: `Resubscribed via API`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.CampaignManual:
          historyRows.push({
            message: `Opt out removed manually ${optOut.deletedByUser ? `by ${optOut.deletedByUser?.fullName}` : ""}`,
            timestamp: optOut.deletedAt,
          });
          break;
        case OptOutDeleteSourceType.ShortCodeKeyword:
          historyRows.push({
            message: `Resubscribed via short code keyword`,
            timestamp: optOut.deletedAt,
          });
          break;
        default:
          deleteType satisfies never;
      }
    }
  });

  historyRows.sort((a, b) => b.timestamp - a.timestamp);

  return (
    <FlexBox direction="column" gap={3} data-testid="subscriber-details">
      <ContactFieldsEditor
        fields={subscriber.fields}
        editing={editing}
        control={control}
        fieldDefinitions={fieldDefinitions}
        showSaveSuccessful={showSaveSuccessful}
      />

      <Accordion disableGutters sx={{ padding: 0, background: "none" }}>
        <AccordionSummary
          expandIcon={<ChevronDown sx={{ fontSize: "1em" }} />}
          sx={{
            backgroundColor: palette.midGray,
            minHeight: "27px",
            ".MuiAccordionSummary-content": { margin: 0 },
          }}
        >
          <Typography variant="body2Bold">Contact History</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 0,
          }}
        >
          <List sx={{ backgroundColor: palette.lightestGray, px: 2 }}>
            {historyRows.map(row => (
              <ListItem
                key={row.timestamp}
                sx={{
                  px: 0,
                  "&:not(:last-child)": {
                    borderBottom: `1px solid ${palette.lightGray}`,
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2">{row.message}</Typography>
                  }
                  secondary={
                    <Typography variant="body3">
                      {formatShortDateTime(row.timestamp)}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters sx={{ padding: 0, background: "none" }}>
        <AccordionSummary
          expandIcon={<ChevronDown sx={{ fontSize: "1em" }} />}
          sx={{
            backgroundColor: palette.midGray,
            minHeight: "27px",
            ".MuiAccordionSummary-content": { margin: 0 },
          }}
        >
          <Typography variant="body2Bold">Threads</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: palette.lightestGray, px: 2 }}>
          <AsyncWrapper
            data={getThreadsQuery.data}
            loading={getThreadsQuery.loading}
            errors={getThreadsQuery.error}
          >
            {data =>
              data.getThreadsForSubscriber.length ? (
                <Table
                  rows={data.getThreadsForSubscriber}
                  columns={[
                    {
                      key: "sender",
                      title: "Number",
                      renderValue: thread =>
                        thread.fromOwnedPhoneNumber.phoneNumber,
                    },
                    {
                      key: "firstMessageAt",
                      title: "First Message",
                      renderValue: thread =>
                        thread.firstMessageAt
                          ? formatShortDateTime(thread.firstMessageAt)
                          : "",
                    },
                    {
                      key: "lastMessageAt",
                      title: "Last Message",
                      renderValue: thread =>
                        thread.firstMessageAt
                          ? formatShortDateTime(thread.lastMessageAt)
                          : "",
                    },
                  ]}
                  rowActions={[
                    {
                      type: "link",
                      id: "go-to-thread",
                      icon: <ChatBubble />,
                      href: thread =>
                        subaccountLink(
                          `/threads?search_type=contact&search=${encodeURIComponent(subscriber.phoneNumber)}&thread=${thread.id}`
                        ),
                      target: "_blank",
                    },
                  ]}
                  noFooter
                />
              ) : (
                <FlexBox justifyContent="center">
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", color: palette.darkGray }}
                  >
                    This subscriber doesn't have any threads yet
                  </Typography>
                </FlexBox>
              )
            }
          </AsyncWrapper>
        </AccordionDetails>
      </Accordion>
    </FlexBox>
  );
}

export default function SubscriberDialog({
  subscriber,
  updateSubscriber,
  subscriberAudience,
  audienceContactFields,
  onClose,
}: {
  subscriber: Subscriber;
  updateSubscriber: (subscriber: Subscriber) => void;
  subscriberAudience: Audience;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  onClose: () => void;
}): ReactElement {
  const formatErrorMessage = useErrorMessageFormatter();
  const isArchived = !!subscriberAudience.archivedAt;
  const initialDialogPageState = isArchived ? "archived" : "details";
  const [dialogPage, setDialogPage] = useState<
    "details" | "optout" | "optin" | "archived"
  >(initialDialogPageState);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [showSaveSuccessful, setShowSaveSuccessful] = useState(false);

  const [editing, setEditing] = useState(false);

  const initialValues = createUpdateSubscriberInitialValues(
    subscriber.id,
    subscriber.fields,
    audienceContactFields
  );

  const methods = useForm<UpdateSubscriberSchemaType>({
    resolver: zodResolver(UpdateSubscriberSchema(audienceContactFields)),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: initialValues,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [updateSubscriberMutation] = useMutation<
    UpdateSubscriberMutation,
    UpdateSubscriberMutationVariables
  >(UPDATE_SUBSCRIBER, {
    refetchQueries: [GET_SUBSCRIBER_SUMMARY],
    awaitRefetchQueries: true,
  });

  const [optOut] = useMutation<
    UnsubscribeSubscriberMutation,
    UnsubscribeSubscriberMutationVariables
  >(UNSUBSCRIBE_SUBSCRIBER, {
    variables: {
      input: {
        audienceId: subscriberAudience.id,
        subscriberId: subscriber.id,
      },
    },
    refetchQueries: [GET_SUBSCRIBER_SUMMARY],
    awaitRefetchQueries: true,
  });

  const [optIn] = useMutation<
    ResubscribeSubscriberMutation,
    ResubscribeSubscriberMutationVariables
  >(RESUBSCRIBE_SUBSCRIBER, {
    variables: {
      input: {
        audienceId: subscriberAudience.id,
        subscriberId: subscriber.id,
      },
    },
    refetchQueries: [GET_SUBSCRIBER_SUMMARY],
    awaitRefetchQueries: true,
  });

  const handleOptOut = async () => {
    setMutationLoading(true);
    setMutationError(null);

    const { errors, data } = await optOut();
    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      setDialogPage("details");
      updateSubscriber(data!.unsubscribeSubscriber);
    }

    setMutationLoading(false);
  };

  const handleOptIn = async () => {
    setMutationLoading(true);
    setMutationError(null);

    const { errors, data } = await optIn();
    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      setDialogPage("details");
      updateSubscriber(data!.resubscribeSubscriber);
    }

    setMutationLoading(false);
  };

  const handleUpdateSubmit = async (rawValues: unknown) => {
    const values = UpdateSubscriberSchema(audienceContactFields).parse(
      rawValues
    );
    setMutationLoading(true);
    setMutationError(null);

    const { errors, data } = await updateSubscriberMutation({
      variables: {
        input: {
          audienceId: subscriberAudience.id,
          subscriberId: values.subscriberId,
          fields: JSON.stringify(values.fields),
        },
      },
    });

    if (errors) {
      setMutationError(formatErrorMessage(errors));
    } else {
      updateSubscriber(data!.updateSubscriber);
      setEditing(false);
      setShowSaveSuccessful(true);
    }

    setMutationLoading(false);
  };

  function renderArchivedSubscriber() {
    return (
      <>
        <DialogContent sx={{ px: 5 }}>
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          <SubscriberDetails
            subscriber={subscriber}
            subscriberAudience={subscriberAudience}
            editing={false}
            control={control}
            fieldDefinitions={audienceContactFields}
            showSaveSuccessful={showSaveSuccessful}
          />
        </DialogContent>
        <SubscriberDialogActions sx={{ px: 5 }}>
          <Button onClick={onClose} variant="contained" color="tertiary">
            Close
          </Button>
        </SubscriberDialogActions>
      </>
    );
  }

  function renderDetails() {
    return (
      <>
        <DialogContent sx={{ px: 5 }}>
          {editing && <RhfErrorBanner control={control} />}
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          <SubscriberDetails
            subscriber={subscriber}
            subscriberAudience={subscriberAudience}
            editing={editing}
            control={control}
            fieldDefinitions={audienceContactFields}
            showSaveSuccessful={showSaveSuccessful}
          />
        </DialogContent>
        <SubscriberDialogActions sx={{ px: 5 }}>
          {subscriber.optOuts.find(o => !o.deletedAt) ? (
            <Button
              onClick={() => setDialogPage("optin")}
              color="primary"
              variant="outlined"
            >
              Opt In
            </Button>
          ) : (
            <Button
              onClick={() => setDialogPage("optout")}
              color="error"
              variant="outlined"
            >
              Opt Out
            </Button>
          )}
          <FlexBox gap={0.5}>
            <Button onClick={onClose} variant="contained" color="tertiary">
              Close
            </Button>
            {editing ? (
              <>
                <Button
                  onClick={handleSubmit(handleUpdateSubmit)}
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting || mutationLoading}
                >
                  {isSubmitting || mutationLoading ? "Saving..." : "Save"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  setShowSaveSuccessful(false);
                  setEditing(true);
                }}
                color="primary"
                variant="contained"
              >
                Edit
              </Button>
            )}
          </FlexBox>
        </SubscriberDialogActions>
      </>
    );
  }

  function renderOptOut() {
    return (
      <>
        <DialogContent>
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          <Typography>
            Are you sure you want to unsubscribe this contact? They will be sent
            your short code's opt out message: "
            {subscriberAudience.shortCodeConfig!.optOutMessage}"
          </Typography>
        </DialogContent>
        <SubscriberDialogActions>
          <Button
            onClick={() => {
              setDialogPage("details");
              setMutationError(null);
            }}
            disabled={mutationLoading}
            color="tertiary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOptOut}
            color="error"
            disabled={mutationLoading}
            variant="outlined"
          >
            {mutationLoading ? "Opting Out..." : "Opt Out"}
          </Button>
        </SubscriberDialogActions>
      </>
    );
  }

  function renderOptIn() {
    return (
      <>
        <DialogContent>
          {mutationError && <Alert severity="error">{mutationError}</Alert>}
          <Typography>
            Are you sure you want to resubscribe this contact? They will be sent
            your short code's welcome message: "
            {subscriberAudience.shortCodeConfig!.optInMessage}"
          </Typography>
        </DialogContent>
        <SubscriberDialogActions>
          <Button
            onClick={() => {
              setDialogPage("details");
              setMutationError(null);
            }}
            disabled={mutationLoading}
            color="tertiary"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOptIn}
            color="primary"
            variant="contained"
            disabled={mutationLoading}
          >
            {mutationLoading ? "Opting In..." : "Opt In"}
          </Button>
        </SubscriberDialogActions>
      </>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testId="edit-subscriber-details-dialog"
    >
      <DialogTitle sx={{ px: 5 }}>
        Subscriber Details for{" "}
        <strong>{prettyPhoneNumber(subscriber.phoneNumber)}</strong>
      </DialogTitle>
      {dialogPage === "archived" && renderArchivedSubscriber()}
      {dialogPage === "details" && renderDetails()}
      {dialogPage === "optout" && renderOptOut()}
      {dialogPage === "optin" && renderOptIn()}
    </Dialog>
  );
}
